/**
 * WITC e múltipla escolha partilham a mesma árvore de ficheiros estáticos:
 * `public/assets/images/{basePath}/{key}.webp` ou `.png`.
 * Prefer WebP na UI; fallback para PNG se o WebP não existir.
 */

export const QUIZ_QUESTION_IMAGES_ROOT = '/assets/images';

/**
 * Filtro CSS da silhueta WITC (mesmo em WitcQuizPlayer, PvP WITC e modal de roster):
 * imagem a cores → preto sólido mantendo o canal alpha.
 */
export const WITC_SILHOUETTE_FILTER_CSS = 'brightness(0)';

/**
 * Optional absolute origin when quiz images are not served by the same host as the SPA
 * (e.g. CDN or separate static server). Example: https://cdn.example.com — no trailing slash.
 */
function quizImageAssetOriginPrefix(): string {
  const raw = process.env.REACT_APP_IMAGE_ASSET_ORIGIN;
  if (raw == null || !String(raw).trim()) return '';
  return String(raw).trim().replace(/\/$/, '');
}

export function quizQuestionImageUrlsFromKey(
  basePath: string | null | undefined,
  key: string | null | undefined
): { webp: string; png: string } | null {
  const b = sanitizePathSegment(basePath);
  const k = sanitizeFileKey(key);
  if (!b || !k) return null;
  const origin = quizImageAssetOriginPrefix();
  const pub = origin ? '' : process.env.PUBLIC_URL || '';
  const pathAfterOrigin = `${pub}${QUIZ_QUESTION_IMAGES_ROOT}/${b}/${k}`;
  const base = origin ? `${origin}${pathAfterOrigin}` : pathAfterOrigin;
  return { webp: `${base}.webp`, png: `${base}.png` };
}

/** URL única (PNG). Preferir {@link quizQuestionImageUrlsFromKey} com fallback WebP→PNG no player. */
export function quizQuestionImageSrcFromKey(
  basePath: string | null | undefined,
  key: string | null | undefined
): string | null {
  return quizQuestionImageUrlsFromKey(basePath, key)?.png ?? null;
}

function sanitizePathSegment(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  const t = raw.trim().replace(/\\/g, '/');
  if (t.includes('..')) return null;
  const segments = t.split('/').filter(Boolean);
  for (const seg of segments) {
    if (!/^[a-zA-Z0-9_-]+$/.test(seg)) return null;
  }
  return segments.join('/');
}

function sanitizeFileKey(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  let s = raw.trim();
  const lower = s.toLowerCase();
  if (lower.endsWith('.png')) s = s.slice(0, -4).trim();
  else if (lower.endsWith('.webp')) s = s.slice(0, -5).trim();
  if (!s || !/^[a-zA-Z0-9_-]+$/.test(s)) return null;
  return s;
}

/** Mesmas regras que as chaves de imagem nas perguntas: basename, `.png`/`.webp` opcionais removidos. */
export function sanitizeImageAssetFileKey(raw: string | null | undefined): string | null {
  return sanitizeFileKey(raw);
}

/**
 * Pasta em `public/assets/images/` para perguntas WITC/MC — alinhado ao servidor
 * {@code ResolveLobbyCharacterUnlockImageBasePath} e ao player (`soloQuestionImageBasePath || quiz…`).
 * Usa `||` entre MC e WITC para não ficar preso a `""` (diferente de `??` só com null).
 */
export function resolveQuizImageAssetsFolder(quiz: {
  format?: string | null;
  witcImageAssetsBasePath?: string | null;
  mcImageAssetsBasePath?: string | null;
}): string {
  const fmt = (quiz.format ?? '').trim().toLowerCase();
  const witc = (quiz.witcImageAssetsBasePath ?? '').trim();
  const mc = (quiz.mcImageAssetsBasePath ?? '').trim();
  if (fmt === 'witc') return witc;
  if (mc) return mc;
  return witc;
}

function pickStringField(source: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    const v = source[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

/** Tile Match: session path from start, then quiz folder (camelCase or PascalCase from API). */
export function resolveTileMatchImageAssetsFolder(
  quiz: {
    tileMatchImageAssetsBasePath?: string | null;
    geniusImageAssetsBasePath?: string | null;
    witcImageAssetsBasePath?: string | null;
  },
  sessionBasePath?: string | null
): string {
  const session = (sessionBasePath ?? '').trim();
  if (session) return session;

  const q = quiz as Record<string, unknown>;
  const tileMatch = pickStringField(
    q,
    'tileMatchImageAssetsBasePath',
    'TileMatchImageAssetsBasePath'
  );
  if (tileMatch) return tileMatch;

  return '';
}
